import React, { FC, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { notification } from 'src/common';
import { useTranslation } from 'react-i18next';
import { useTitle } from 'react-use';
import { useApiTender } from 'src/models/procurements/Tender/hooks';
import { useUpdateTender, getUpdateCacheOnUpdateIsReadTender } from 'src/models/procurements/hooks';
import { TenderProvider } from 'src/models/procurements/Tender/providers';
import TenderContent from './Content';
import useCurrentTenderId from 'src/reactiveVars/CurrentTenderIdVar';
import useCurrentBidId from 'src/reactiveVars/CurrentBidIdVar';
import { isFieldString } from 'src/models/procurements/Tender/types';
import { TenderBox } from 'src/models/procurements';
import { Paths } from '../paths';
import { useFeatureFlag, FeatureFlag } from 'src/helpers/featureFlag';
import { router } from 'src';
import { BidChecklist } from 'src/shared';

export const TenderPage: FC = () => {
  const { id } = useParams<{ id: string }>();
  const [, setTenderId] = useCurrentTenderId();
  const [bidId, setBidId] = useCurrentBidId();
  const { data: apiData, refetch } = useApiTender(id);
  const isEditableTender = useFeatureFlag(FeatureFlag.EditableDetailspage);
  const { t } = useTranslation();
  const [markAsRead] = useUpdateTender();

  useEffect(() => {
    const unlisten = router.subscribe(state => {
      if (
        state.location.pathname &&
        state.location.pathname.split('/')[1] !== Paths.TENDER_PAGE.split('/')[1] &&
        isEditableTender &&
        apiData &&
        !!apiData.bids.length
      ) {
        refetch();
      }
    });

    return () => {
      unlisten();
    };
  }, [apiData, isEditableTender, refetch]);

  useEffect(() => {
    setTenderId(id);
  }, [id, setTenderId]);

  useEffect(() => {
    if (apiData?.isRead === false && id) {
      markAsRead({
        variables: { tenderId: id, isRead: true },
        update: getUpdateCacheOnUpdateIsReadTender(id, true)
      }).catch(() => {
        notification.error({
          description: t('Common.unknownErrorDesc'),
          message: t('Common.unknownError')
        });
      });
    }
  }, [apiData, id, markAsRead, t]);

  const title = useMemo(() => {
    const titleBox = apiData?.general.name && new TenderBox(apiData.general.name);
    return titleBox && isFieldString(titleBox.firstField) && !!titleBox?.firstField.string
      ? titleBox.firstField.string
      : document.title;
  }, [apiData]);

  useEffect(() => {
    setBidId(apiData && !!apiData.bids.length ? apiData.bids[0].id : undefined);
  }, [apiData, id, setBidId, setTenderId]);

  useTitle(title, { restoreOnUnmount: true });

  return (
    <TenderProvider>
      <TenderContent />
      {bidId && <BidChecklist bidId={bidId} />}
    </TenderProvider>
  );
};

export default TenderPage;
